import {
    Strategy,
    StrategyExitCondition,
    StrategyIndicatorInstance,
    StrategyInput,
    StrategyInputNode,
    StrategyNodeAttributeTemplate,
    StrategyNodeTemplate,
    StrategyOptimisation,
    StrategyTemplate,
    StrategyTemplateFieldOverride,
} from '~/api-services/AIStrategyBuilderService/types';

import { OptimisationState, RuleState } from './types';

// Strip the rule-number namespace from a cloned node name, e.g. "r.1.SMA.short" -> "SMA.short".
// Tolerates the legacy bare-number prefix ("1.SMA.short") and connection double-number prefixes
// ("r.1.1.if.buy" -> "if.buy"). Optimisation nodes use a non-numeric "o.<code>." namespace and are
// matched by their code instead (see optimisationStatesFromCloneNodes).
export const stripRulePrefix = (name: string): string => name.replace(/^(?:r\.)?\d+\.(?:\d+\.)?/, '');

export function buildAttributeValue(attr: StrategyNodeAttributeTemplate): unknown {
    if (attr.fields && attr.fields.length > 0) {
        const obj: Record<string, unknown> = {};
        for (const child of attr.fields) {
            obj[child.name] = buildAttributeValue(child);
        }
        return obj;
    }
    return attr.defaultValue;
}

export function buildNodeFromTemplate(tpl: StrategyNodeTemplate): Record<string, unknown> {
    const node: Record<string, unknown> = {};
    for (const attr of tpl.fields) {
        node[attr.name] = buildAttributeValue(attr);
    }
    return node;
}

// Apply a template's per-field `defaultValue` overrides onto a seeded node, recursing into
// nested groups (e.g. period.min / period.max). Leaf overrides set the value directly.
export function applyFieldOverrides(
    node: Record<string, unknown>,
    overrides?: StrategyTemplateFieldOverride[]
): Record<string, unknown> {
    if (!overrides || overrides.length === 0) {
        return node;
    }
    const out = { ...node };
    for (const ov of overrides) {
        if (ov.fields && ov.fields.length > 0) {
            const child =
                out[ov.name] && typeof out[ov.name] === 'object' ? (out[ov.name] as Record<string, unknown>) : {};
            out[ov.name] = applyFieldOverrides(child, ov.fields);
        } else {
            out[ov.name] = ov.defaultValue;
        }
    }
    return out;
}

// Seed an editable node for an indicator instance: the instance template's defaults + its
// own code as the node name.
export function seedNodeFromInstances(
    indicatorInstances: Record<string, StrategyIndicatorInstance>,
    code: string
): Record<string, unknown> | null {
    const instance = indicatorInstances[code];
    if (!instance) {
        return null;
    }
    const node = buildNodeFromTemplate(instance);
    node.name = code;
    return node;
}

// Clone seeding: rebuild the wizard rules from a cloned strategy's input nodes. Indicator nodes
// are grouped by their rule-number prefix (e.g. "r.2.SMA.short" -> rule 2); cloned indicator
// nodes are emitted in combination order, so each group's order is preserved.
export function rulesFromCloneNodes(cloneNodes: StrategyInputNode[]): RuleState[] {
    const indicatorNodes = cloneNodes.filter(
        (n) =>
            n.type !== 'CLOUD' &&
            !(typeof n.x === 'string' && typeof n.y === 'string') &&
            // Optimisation nodes ("o.1.*") are restored separately, not as editable rules.
            !String(n.name ?? '').startsWith('o.')
    );
    const groups = new Map<number, StrategyInputNode[]>();
    for (const n of indicatorNodes) {
        const match = /^(?:r\.)?(\d+)\./.exec(String(n.name ?? ''));
        const ruleNum = match ? Number(match[1]) : 1;
        const list = groups.get(ruleNum) ?? [];
        list.push(n);
        groups.set(ruleNum, list);
    }
    const builtRules: RuleState[] = [];
    for (const ruleNum of [...groups.keys()].sort((a, b) => a - b)) {
        const group = groups.get(ruleNum) ?? [];
        const codes: string[] = [];
        const nodes: Record<string, Record<string, unknown>> = {};
        for (const n of group) {
            if (!n?.name) {
                continue;
            }
            const code = stripRulePrefix(String(n.name));
            codes.push(code);
            nodes[code] = { ...(n as Record<string, unknown>), name: code };
        }
        if (codes.length > 0) {
            builtRules.push({ codes, nodes, editOpen: {} });
        }
    }
    return builtRules;
}

// Clone seeding (optimisations): re-check the optimisation boxes from cloned "o.<code>.*" nodes
// by reading each node's code segment and matching it back to the side's optimisation list.
// Skips "o.<code>.N.*" connection nodes, which carry string x/y refs and are rebuilt from the
// combination.
export function optimisationStatesFromCloneNodes(
    cloneNodes: StrategyInputNode[],
    sideOpts: StrategyOptimisation[]
): Record<number, OptimisationState> {
    const nodesByCode = new Map<string, Record<string, Record<string, unknown>>>();
    for (const n of cloneNodes) {
        const name = String(n.name ?? '');
        const match = /^o\.([^.]+)\./.exec(name);
        if (!match || (typeof n.x === 'string' && typeof n.y === 'string')) {
            continue;
        }
        const code = match[1];
        const indicatorCode = name.replace(/^o\.[^.]+\./, '');
        const group = nodesByCode.get(code) ?? {};
        group[indicatorCode] = { ...(n as Record<string, unknown>), name: indicatorCode };
        nodesByCode.set(code, group);
    }
    const states: Record<number, OptimisationState> = {};
    sideOpts.forEach((o, idx) => {
        const nodes = nodesByCode.get(o.code);
        if (nodes) {
            states[idx] = { nodes, editOpen: {} };
        }
    });
    return states;
}

// Template seeding: rebuild the wizard rules from a template's `rules` (indicator list per rule
// + per-field defaultValue overrides). A rule referencing an unknown indicator instance is
// dropped whole (partial rules would not resolve a combination).
export function rulesFromTemplate(
    template: StrategyTemplate,
    indicatorInstances: Record<string, StrategyIndicatorInstance>
): RuleState[] {
    const builtRules: RuleState[] = [];
    for (const rule of template.rules) {
        const codes: string[] = [];
        const nodes: Record<string, Record<string, unknown>> = {};
        let ok = true;
        for (const inst of rule.indicatorInstances) {
            if (!inst?.code) {
                continue;
            }
            const seed = seedNodeFromInstances(indicatorInstances, inst.code);
            if (!seed) {
                ok = false;
                break;
            }
            codes.push(inst.code);
            nodes[inst.code] = applyFieldOverrides(seed, inst.fields);
        }
        if (ok && codes.length > 0) {
            builtRules.push({ codes, nodes, editOpen: {} });
        }
    }
    return builtRules;
}

// Clone: build a template (Step 1 config) + the original node array (Step 2 rules) from an
// existing strategy's stored input, ready to seed the Create Strategy wizard.
export function cloneSourceFromStrategy(strategy: Strategy): {
    template: StrategyTemplate;
    cloneNodes: StrategyInputNode[];
} {
    const input = strategy.input as StrategyInput | undefined;
    const nodes = input?.strategy ?? [];
    const cloud = nodes.find((n) => n.type === 'CLOUD' || 'exitCondition' in n) as StrategyInputNode | undefined;
    const exit = (cloud?.exitCondition ?? {}) as Partial<StrategyExitCondition>;
    const template: StrategyTemplate = {
        uuid: '',
        name: '',
        instrument: input?.instrument ?? {
            code: strategy.instrument_code,
            period: strategy.instrument_period,
        },
        cloud: {
            quantity: Number(cloud?.quantity ?? 0),
            takeProfit: Number(exit.takeProfit ?? 0),
            stopLoss: Number(exit.stopLoss ?? 0),
        },
        // Step 2 is seeded from cloneNodes; an empty rules list keeps the template seeding a no-op.
        rules: [],
    };
    return { template, cloneNodes: nodes };
}
